/**
 * Client-side RoomOption model (Firestore-backed).
 * Use .get({ tripId, id }) to load a stored room option. No in-memory factory.
 */

import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { Hotel } from './HotelModel.js';
import type { MealCombinationId, RefundablePreference, RoomRateModel } from './RoomRateModel.js';

import { differenceInDays } from 'date-fns';

import { RoomOptionModelSpec } from '@elara/domain/models/RoomOptionModelSpec';
import { occupancyKey } from '@elara/domain/models/schemas/room-schemas';

import { createClientModel } from '#firebase/createClientModel.js';
import { getPriceDelta, isPriceStale, isTravelDatePassed } from '#lib/price-utils.js';
import { pickLeadRate } from '#lib/room-rate-choice.js';

import { createClientHotelModel } from './HotelModel.js';
import { MEAL_COMBINATIONS, RoomRateModelFactory } from './RoomRateModel.js';

export function createClientRoomOptionModel(firestore: FirestoreContainer) {
  const HotelModel = createClientHotelModel(firestore);

  return createClientModel(firestore, RoomOptionModelSpec, () => ({
    get displayName(): string {
      return this.generated?.name ?? this.type ?? 'Unknown Room';
    },

    get allRates(): RoomRateModel[] {
      // Hand each rate the stay it's priced for, so `cancellationPolicyTitle` can resolve a relative
      // deadline ("2 days before arrival") to a date. Only this document knows the search dates.
      const stay = this.search ? { checkInDate: this.search.startDate, checkOutDate: this.search.endDate } : undefined;
      return RoomRateModelFactory.fromMany(this.rates ?? [], stay);
    },

    /**
     * @deprecated rateKey rotates on every Sabre call (search and pricecheck both
     * return fresh keys), so any persisted reference goes stale on the next merge.
     * Use `getRateByIdentity({ productCode, occupancy })` for stored references.
     * Still safe to use within a session for in-memory selection.
     */
    getRateByRateKey(rateKey: string): RoomRateModel | undefined {
      return this.allRates.find((r) => r.rateKey === rateKey);
    },

    /** Returns the rate identified by `(productCode, occupancy)` — the stable identity. */
    getRateByIdentity(identity: { productCode: string; occupancy?: Occupancy }): RoomRateModel | undefined {
      const targetOccKey = identity.occupancy ? occupancyKey(identity.occupancy) : undefined;
      return this.allRates.find((r) => {
        if (r.productCode !== identity.productCode) return false;
        if (!targetOccKey) return true;
        return r.occupancy ? occupancyKey(r.occupancy) === targetOccKey : false;
      });
    },

    /** @deprecated Use `isRateAvailableByIdentity` for persisted references. */
    isRateAvailable(rateKey: string): boolean {
      if (!this.isAvailable) return false;
      const rate = this.getRateByRateKey(rateKey);
      return rate?.available !== false;
    },

    /** Returns false if the room or the rate at `(productCode, occupancy)` is unavailable. */
    isRateAvailableByIdentity(identity: { productCode: string; occupancy?: Occupancy }): boolean {
      if (!this.isAvailable) return false;
      const rate = this.getRateByIdentity(identity);
      return rate ? rate.available !== false : false;
    },

    totalCost(
      quantity: number,
      identity?: { productCode: string; occupancy?: Occupancy } | string,
    ): { amount: number; currency: string } | undefined {
      // Fallback to cheapest rate if no identity is provided
      if (!identity) {
        const cheapest = this.cheapestRate;
        if (!cheapest) return undefined;
        return { amount: cheapest.localPricing.totalPrice * quantity, currency: cheapest.localPricing.currency };
      }

      // Backwards-compat: if a string is passed, treat it as rateKey (deprecated).
      const rate = typeof identity === 'string' ? this.getRateByRateKey(identity) : this.getRateByIdentity(identity);
      if (!rate) return undefined;
      return { amount: rate.localPricing.totalPrice * quantity, currency: rate.localPricing.currency };
    },

    get hasRefundableRates(): boolean {
      return this.allRates.some((r) => r.isRefundable);
    },

    get hasNonRefundableRates(): boolean {
      return this.allRates.some((r) => r.isRefundable === false);
    },

    get cheapestRate(): RoomRateModel | null {
      if (this.allRates.length === 0) return null;
      return this.allRates.reduce((cheapest, current) =>
        current.localPricing.totalPrice < cheapest.localPricing.totalPrice ? current : cheapest,
      );
    },

    /**
     * The rate a card leads with — see `pickLeadRate`: the party the latest search asked about first,
     * only rates still available, and a gated rate only when nothing open is left. Null when every
     * rate is gone (the card is then sold out). Unlike `cheapestRate`, never a dead rate.
     */
    get leadRate(): RoomRateModel | null {
      return this.leadRateFor();
    },

    /**
     * The lead rate (see `leadRate`) for a given party — the lowest bookable rate a saved room is
     * priced at on the shortlist. Falls back to the party the latest search asked about when no party
     * is given, and to any party when the given one has nothing left (the card then says who the price
     * is for). Null when every rate is gone.
     */
    leadRateFor(occupancy?: Occupancy): RoomRateModel | null {
      return pickLeadRate(this.allRates, occupancy ?? this.search?.occupancy) ?? null;
    },

    /**
     * The rate the room's search chose to present it on, when it chose this room: the best-ranked
     * rate carrying a `recommendation` from the doc's own search, while that rate is still available.
     * A room search marks only the rooms it presents (ELARA-1092), so this is how a list of every
     * room from one search tells Elara's picks from the rest.
     *
     * A room that takes the whole party has each rate filed twice, for the adults and for the family, and
     * the pick is on both at one rank: the copy priced for the searched party wins, or the carousel read
     * "Priced for 2 adults" while the guide opened on the family's identical price (Ham Yard Hotel, 2026-10-20).
     */
    get recommendedRate(): RoomRateModel | null {
      const picked = this.allRates.filter(
        (r) => r.recommendation && r.recommendation.searchId === this.searchId && r.available !== false,
      );
      if (picked.length === 0) return null;
      const searched = this.search?.occupancy ? occupancyKey(this.search.occupancy) : undefined;
      const forSearched = (r: RoomRateModel) => !!r.occupancy && occupancyKey(r.occupancy) === searched;
      return picked.reduce((best, r) => {
        const rank = r.recommendation?.rank ?? 99;
        const bestRank = best.recommendation?.rank ?? 99;
        if (rank !== bestRank) return rank < bestRank ? r : best;
        return forSearched(r) && !forSearched(best) ? r : best;
      });
    },

    /**
     * How many of this room a rate priced for `occupancy` books: the room search's count when Elara picked
     * the room as "book N of these" (a family on per-room adult rates), for rates priced for the same party
     * as the picked one, and one otherwise. A rate priced for the whole party already covers everyone.
     */
    roomsToBook(occupancy: Occupancy | undefined): number {
      const pick = this.recommendedRate;
      const rooms = pick?.recommendation?.rooms ?? 1;
      if (rooms < 2 || !occupancy || !pick?.occupancy) return 1;
      return occupancyKey(occupancy) === occupancyKey(pick.occupancy) ? rooms : 1;
    },

    get nightsCount(): number {
      const search = this.search;
      if (!search?.startDate || !search?.endDate) return 0;
      return differenceInDays(new Date(search.endDate), new Date(search.startDate));
    },

    get cost(): { amount: number; currency: string } | undefined {
      const rate = this.cheapestRate;
      if (!rate) return undefined;
      return { amount: rate.localPricing.totalPrice, currency: rate.localPricing.currency };
    },

    /** Price delta for the cheapest rate vs its original price from priceHistory. */
    get priceDelta() {
      const rate = this.cheapestRate;
      if (!rate) return null;
      const history = rate.priceHistory;
      if (!history || history.length === 0) return null;
      const originalPricing = history.at(-1)!.pricing;
      return getPriceDelta(
        { amount: rate.localPricing.totalPrice, currency: rate.localPricing.currency },
        { amount: originalPricing.totalPrice, currency: originalPricing.currency },
      );
    },

    /** Price history from the cheapest rate (for date display in tooltips). */
    get priceHistory() {
      return this.cheapestRate?.priceHistory;
    },

    get availableMealCombinations() {
      return MEAL_COMBINATIONS.map((combo) => ({
        id: combo.id,
        hasCombination: this.allRates.some((r) => r.matchesMealCombination(combo.id)),
      }));
    },

    get initialPreferences(): { refundable: RefundablePreference; meal: MealCombinationId } {
      const cheapest = this.cheapestRate;
      if (!cheapest) return { refundable: 'non-refundable', meal: 'none' };
      const refundable: RefundablePreference = cheapest.isRefundable ? 'refundable' : 'non-refundable';
      const meal = MEAL_COMBINATIONS.find((c) => cheapest.matchesMealCombination(c.id))?.id ?? 'none';
      return { refundable, meal };
    },

    get isAvailable(): boolean {
      return this.available !== false;
    },

    get isPartiallyAvailable(): boolean {
      return this.partiallyAvailable === true;
    },

    // Compound, read-time staleness: a departed trip never needs a price recheck, so
    // short-circuit before the time-threshold check. Never denormalized (goes stale on read).
    get isStale(): boolean {
      if (this.isTravelDatePassed) return false;
      return isPriceStale(this.fetchedAt);
    },

    /**
     * True iff the room-level enrichment block AND every rate's enrichment block
     * have a `generatedAt` timestamp. RecheckRoomPrice clears per-rate `generated`
     * when pricing shifts, so per-rate freshness must gate this — otherwise stale
     * deposit/cancellation text (which embeds prices) would survive a recheck.
     */
    get isEnriched(): boolean {
      if (!this.generated?.generatedAt) return false;
      return this.allRates.every((r) => !!r.generated?.generatedAt);
    },

    /** True if the check-in date is today or in the past. */
    get isTravelDatePassed(): boolean {
      return isTravelDatePassed(this.search?.startDate);
    },

    get unavailableRates(): RoomRateModel[] {
      return this.allRates.filter((r) => r.available === false);
    },

    get availableRates(): RoomRateModel[] {
      return this.allRates.filter((r) => r.available !== false);
    },

    get maxAvailableQuantity(): number | undefined {
      const max = this.allRates.reduce((max, rate) => Math.max(max, rate.availableQuantity ?? 0), 0);
      return max || undefined;
    },

    /**
     * Lazily hydrate the related hotel.
     * Returns undefined until the document is ready or on error.
     */
    get hotel(): Hotel | undefined {
      const hotelId = this.hotelId;
      if (!hotelId) return undefined;
      const hotel = HotelModel.get({ id: hotelId });
      if (hotel.error) {
        console.warn(`[RoomOption] Failed to load hotel ${hotelId}:`, hotel.error);
        return undefined;
      }
      return hotel.data;
    },

    /** True once the related hotel doc has resolved (loaded, errored, or no hotelId to load). */
    get hotelReady(): boolean {
      const hotelId = this.hotelId;
      if (!hotelId) return true;
      const handle = HotelModel.get({ id: hotelId });
      return handle.isReady || handle.error != null;
    },

    /** For itinerary/shortlist: referenceId is the RoomOption document id. */
    referenceId(_hotelId: string, _rate?: RoomRateModel): string {
      return this.id;
    },
  }));
}

export type ClientRoomOptionModel = ReturnType<typeof createClientRoomOptionModel>;
export type RoomOptionModel = ClientInstanceOf<ClientRoomOptionModel>;
